import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CalendarPlus, CheckCircle2, Eye, Import, Loader2, Pencil, Plus, Trash2, UserRound, Users } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Avatar, Badge, Button, Modal, Pagination, Select, StatCard } from "../../components/ui";
import { adminSession, apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { Shooter } from "../../data/types";
import { usePortalLink } from "../../utils/portalLink";

const emptyForm: Partial<Shooter> = {
  sid: "",
  name: "",
  email: "",
  club: "",
  association: "Australian Shooting Association",
  state: "Queensland",
  country: "Australia",
  grade: "C",
  rifleClass: "Open",
  category: "Rifle Shooter",
  handedness: "Right Handed",
  status: "Active",
};

export default function AdminCompetitors() {
  const navigate = useNavigate();
  const link = usePortalLink();
  const { data: allShooters, reload } = useApi<Shooter[]>("/shooters", []);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  // Linked to from Classes & Grades, to find who holds an unexpected grade.
  const [params] = useSearchParams();
  const [classFilter, setClassFilter] = useState(params.get("class") || "All Classes");
  const [gradeFilter, setGradeFilter] = useState(params.get("grade") || "All Grades");
  const q = search.trim().toLowerCase();
  const shooters = allShooters.filter(
    (s) =>
      (!q || s.name.toLowerCase().includes(q) || s.sid.includes(q) || s.club.toLowerCase().includes(q)) &&
      (statusFilter === "All Status" || s.status === statusFilter) &&
      // A competitor entered in two classes matches on either of them, and on
      // the grade they hold in either — one row can no longer hide the second.
      (classFilter === "All Classes" || (s.classes?.length ? s.classes : [s.rifleClass]).includes(classFilter)) &&
      (gradeFilter === "All Grades" ||
        s.grade === gradeFilter ||
        Object.values(s.gradeByClass || {}).includes(gradeFilter))
  );

  /*
   * Drawn from the register rather than a fixed list, so a class or grade that
   * exists in the data can always be filtered for — including an unexpected one
   * arriving from the Classes page to be found and corrected.
   */
  const classOptions = useMemo(
    () =>
      Array.from(new Set(allShooters.flatMap((s) => (s.classes?.length ? s.classes : [s.rifleClass])).filter(Boolean))).sort(),
    [allShooters]
  );
  const gradeOptions = useMemo(
    () =>
      Array.from(
        new Set(allShooters.flatMap((s) => [s.grade, ...Object.values(s.gradeByClass || {})]).filter(Boolean))
      ).sort(),
    [allShooters]
  );
  // Selection follows the filtered list, so "select all" after a search targets
  // exactly what is on screen rather than the whole register.
  const selection = useTableSelection(shooters.map((s) => s.sid));
  const [open, setOpen] = useState(false);
  const [editingSid, setEditingSid] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Shooter>>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k: keyof Shooter, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm(emptyForm);
    setEditingSid(null);
    setError("");
    setOpen(true);
  };
  const openEdit = (s: Shooter) => {
    setForm(s);
    setEditingSid(s.sid);
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (!form.sid || !form.name) {
      setError("SID and full name are required — the SID is the permanent Association-issued identifier.");
      return;
    }
    if (!adminSession.token()) {
      setError("Sign in first (admin / admin123) — changes are authenticated.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingSid) await apiPut(`/admin/shooters/${editingSid}`, form);
      else await apiPost("/admin/shooters", form);
      setOpen(false);
      reload();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  /*
   * One code path for removing one row or many: a single-row delete is just a
   * pending list of one. That keeps the confirmation, the progress state and the
   * error handling identical however it was started.
   */
  const [pending, setPending] = useState<Shooter[]>([]);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const removeSelected = async () => {
    setRemoving(true);
    const failed: string[] = [];
    for (const s of pending) {
      try {
        await apiDelete(`/admin/shooters/${s.sid}`);
      } catch (e: any) {
        failed.push(`${s.name}: ${e.message}`);
      }
    }
    setRemoving(false);
    setPending([]);
    selection.clear();
    reload();
    setNotice(
      failed.length
        ? `${pending.length - failed.length} of ${pending.length} removed. ${failed[0]}`
        : `${pending.length} competitor${pending.length === 1 ? "" : "s"} removed.`
    );
  };

  return (
    <div>
      <AdminTopbar
        title="Competitors"
        subtitle="Manage all shooters / competitors in the system."
        actions={
          <>
            <Button onClick={openCreate}><Plus size={16} /> Add Competitor</Button>
            <Link to="/admin/import"><Button variant="secondary"><Import size={16} /> Import File</Button></Link>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={<Users size={20} />} value={allShooters.length.toLocaleString()} label="Total Competitors" sub="On the SID register" />
        <StatCard icon={<CalendarPlus size={20} />} value={allShooters.filter((x) => x.status === "Active").length.toLocaleString()} label="Active" iconBg="bg-success-soft text-success" />
        <StatCard icon={<Users size={20} />} value={allShooters.filter((x) => (x.classes?.length ?? 0) > 1).length.toLocaleString()} label="Multi-Class Entries" sub="Entered in 2+ classes" iconBg="bg-warning-soft text-warning" />
        <StatCard icon={<UserRound size={20} />} value={allShooters.filter((x) => x.roles?.length).length.toLocaleString()} label="Officials" sub="Match Director, CRO..." iconBg="bg-violet-50 text-violet-600" />
      </div>

      <div className="card mt-4 flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[280px] flex-1">
          <label className="label">Search</label>
          <input
            className="input"
            placeholder="Search by name, SID or club..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Status</label>
          <Select options={["All Status", "Active", "Inactive"]} value={statusFilter} onChange={setStatusFilter} className="w-full sm:w-36" />
        </div>
        <div>
          <label className="label">Class</label>
          <Select options={["All Classes", ...classOptions]} value={classFilter} onChange={setClassFilter} className="w-full sm:w-36" />
        </div>
        <div>
          <label className="label">Grade</label>
          <Select options={["All Grades", ...gradeOptions]} value={gradeFilter} onChange={setGradeFilter} className="w-full sm:w-32" />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setStatusFilter("All Status");
            setClassFilter("All Classes");
            setGradeFilter("All Grades");
          }}
        >
          Reset
        </Button>
      </div>

      {notice && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-success/30 bg-success-soft px-4 py-2.5 text-[13px] font-semibold text-success">
          <CheckCircle2 size={16} /> {notice}
          <button className="ml-auto font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <BulkBar
        count={selection.count}
        noun="competitor"
        onClear={selection.clear}
        onDelete={() => setPending(shooters.filter((s) => selection.isSelected(s.sid)))}
      />

      <div className="card mt-4">
        <div className="table-scroll [--table-offset:380px]">
          <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th w-10">
                <SelectAllBox
                  checked={selection.allSelected}
                  indeterminate={selection.someSelected}
                  onChange={selection.toggleAll}
                  label="Select all competitors"
                />
              </th>
              <th className="th">SID</th>
              <th className="th">Competitor</th>
              <th className="th">Country</th>
              <th className="th">State</th>
              <th className="th">Club</th>
              <th className="th">Category</th>
              <th className="th">Classes &amp; Grades</th>
              <th className="th">Status</th>
              <th className="th">Joined</th>
              <th className="th text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!shooters.length && (
              <tr>
                <td colSpan={11} className="td py-10 text-center text-muted">
                  No competitors yet — import a shoot workbook to populate the portal.
                </td>
              </tr>
            )}
            {shooters.map((s) => (
              <tr
                key={s.sid}
                onClick={() => navigate(link(`/shooters/${s.sid}`))}
                className={`cursor-pointer transition-colors ${selection.isSelected(s.sid) ? "bg-primary-soft/50" : "hover:bg-slate-50"}`}
              >
                <td className="td" onClick={(e) => e.stopPropagation()}>
                  <RowBox checked={selection.isSelected(s.sid)} onChange={() => selection.toggle(s.sid)} label={`Select ${s.name}`} />
                </td>
                <td className="td font-bold">{s.sid}</td>
                <td className="td">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={s.name} size={24} />
                    <span className="font-bold">{s.name}</span>
                  </div>
                </td>
                {/* The state code is what the entry list carries and what every
                    other sheet keys on; the full name is the readable form. */}
                <td className="td text-slate-600">{s.country || "—"}</td>
                <td className="td text-slate-600">
                  {s.stateCode || "—"}
                  {s.state && s.state !== s.stateCode && (
                    <span className="ml-1.5 text-[12px] text-muted">{s.state}</span>
                  )}
                </td>
                <td className="td text-slate-600">
                  {s.club}
                  {s.clubName && <span className="ml-1.5 text-[12px] text-muted">{s.clubName}</span>}
                </td>
                <td className="td text-slate-600">{s.category || "—"}</td>
                {/*
                  Every class the competitor is entered in, with the grade they
                  hold in each. One row per shooter showing only the first class
                  hid the second entry entirely — a shooter down for TR and F-TR
                  looked as though the F-TR entry had not imported.
                */}
                <td className="td">
                  <span className="flex flex-wrap gap-1">
                    {(s.classes?.length ? s.classes : [s.rifleClass]).filter(Boolean).map((c) => (
                      <span key={c} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[12px] font-semibold text-ink">
                        {c}
                        <span className="ml-1 text-muted">{s.gradeByClass?.[c] || s.grade}</span>
                      </span>
                    ))}
                  </span>
                </td>
                <td className="td"><Badge tone={s.status === "Active" ? "active" : "inactive"}>{s.status}</Badge></td>
                <td className="td text-muted">{s.memberSince}</td>
                <td className="td text-right" onClick={(e) => e.stopPropagation()}>
                  <span className="inline-flex items-center gap-1.5">
                    <button onClick={() => openEdit(s)} className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50 hover:text-ink" aria-label="Edit shooter">
                      <Pencil size={14} />
                    </button>
                    <Link to={link(`/shooters/${s.sid}`)} className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-slate-50 hover:text-ink" aria-label="View shooter">
                      <Eye size={14} />
                    </Link>
                    <button onClick={() => setPending([s])} className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-danger-soft hover:text-danger" aria-label="Delete shooter">
                      <Trash2 size={14} />
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="border-t border-line">
          <Pagination summary={`${shooters.length} ${shooters.length === 1 ? "competitor" : "competitors"}`} />
        </div>
      </div>

      <ConfirmDialog
        open={pending.length > 0}
        busy={removing}
        title={pending.length === 1 ? "Remove this competitor?" : `Remove ${pending.length} competitors?`}
        intro={
          pending.length === 1
            ? "This takes the competitor off the register."
            : "This takes these competitors off the register."
        }
        items={pending.map((s) => `${s.name} — SID ${s.sid}`)}
        note={
          <>
            A SID is a permanent identifier, so removing someone here does not delete the results they have already shot —
            those stay attached to their SID. Re-importing a workbook that lists them puts them back on the register.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove competitor" : `Remove ${pending.length} competitors`}
        onConfirm={removeSelected}
        onCancel={() => setPending([])}
      />

      <Modal
        title={editingSid ? `Edit Shooter (SID: ${editingSid})` : "Add Competitor"}
        open={open}
        onClose={() => setOpen(false)}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy}>
              {busy && <Loader2 size={15} className="animate-spin" />}
              {editingSid ? "Save Changes" : "Add Competitor"}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">SID <span className="text-danger">*</span></label>
            <input
              className="input disabled:bg-slate-50 disabled:text-muted"
              value={form.sid || ""}
              onChange={(e) => set("sid", e.target.value)}
              disabled={!!editingSid}
              placeholder="Association-issued, permanent"
            />
            {editingSid && <p className="mt-1 text-xs text-muted">The SID is permanent and cannot be changed.</p>}
          </div>
          <div>
            <label className="label">Full Name <span className="text-danger">*</span></label>
            <input className="input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="label">Email</label>
            <input className="input" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <label className="label">Club</label>
            <input className="input" value={form.club || ""} onChange={(e) => set("club", e.target.value)} />
          </div>
          <div>
            <label className="label">State</label>
            <Select
              options={["Queensland", "New South Wales", "Victoria", "South Australia", "Western Australia", "Tasmania"]}
              value={form.state}
              onChange={(v) => set("state", v)}
            />
          </div>
          <div>
            <label className="label">Rifle Class</label>
            <Select options={["Open", "Standard", "F-TR", "F-Open", "Service Rifle"]} value={form.rifleClass} onChange={(v) => set("rifleClass", v)} />
          </div>
          <div>
            <label className="label">Grade</label>
            <Select options={["A", "B", "C"]} value={form.grade} onChange={(v) => set("grade", v as any)} />
          </div>
          <div>
            <label className="label">Status</label>
            <Select options={["Active", "Inactive"]} value={form.status} onChange={(v) => set("status", v as any)} />
          </div>
          <div>
            <label className="label">Handedness</label>
            <Select options={["Right Handed", "Left Handed"]} value={form.handedness} onChange={(v) => set("handedness", v as any)} />
          </div>
          <div>
            <label className="label">Category</label>
            <input className="input" value={form.category || ""} onChange={(e) => set("category", e.target.value)} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
